import React, {
    createContext,
    Dispatch,
    ReactNode,
    SetStateAction,
    useCallback,
    useContext,
    useEffect,
    useState
} from 'react';

import { listIssueType } from 'services/Issue.service';

export type Value = {
  isLoading: boolean;
  types: Issue.Type[];
  filteredTypes: Issue.Type[];
  search: string;
  setSearch: Dispatch<SetStateAction<string>>;
  setFilteredTypes: Dispatch<SetStateAction<Issue.Type[]>>;
  fetchTypes: () => Promise<void>;
};

export type SettingsContextProps = {
  children: (value: Value) => ReactNode;
};

const SettingsContext = createContext<Value | null>(null);

export function SettingsContextProvider({ children }: SettingsContextProps) {

    const [isLoading, setIsLoading] = useState(false);
    const [types, setTypes] = useState<Issue.Type[]>([]);
    const [filteredTypes, setFilteredTypes] = useState<Issue.Type[]>([]);
    const [search, setSearch] = useState('');

    const fetchTypes = async () => {
        setIsLoading(true);

        const response = await listIssueType();

        if (!response.success) {
            setIsLoading(false);
            return;
        }

        setTypes(response.issue_types);
        setFilteredTypes(response.issue_types);
        setIsLoading(false);
    };

    const memoizedFetchTypes = useCallback(fetchTypes, []);

    useEffect(() => {
        memoizedFetchTypes();
    }, [memoizedFetchTypes]);

    useEffect(() => {
        const lower = search.toLowerCase();
        setFilteredTypes(
            types.filter(t => t.name.toLowerCase().includes(lower))
        );
    }, [search, types]);

    const value: Value = {
        isLoading,
        types,
        filteredTypes,
        search,
        setSearch,
        setFilteredTypes,
        fetchTypes: memoizedFetchTypes
    };

    return (
        <SettingsContext.Provider value={value}>
            {children(value)}
        </SettingsContext.Provider>
    );
}

export function useSettingsContext() {
    const context = useContext(SettingsContext);
    if (!context) {
        throw new Error('useSettingsContext must be used within a SettingsContextProvider');
    }
    return context;
}
